import { forwardRef } from 'react'
import type { MomentView } from '../types'

// ============================================================
// MomentCard — 純 overlay（縮圖 backdrop + 文字）
// ------------------------------------------------------------
// 播放交給 Home 層的 <ActivePlayer>，此元件只負責視覺：
//   - 非 active：背景顯示 YouTube 縮圖
//   - active：縮圖淡出（opacity 0）讓底下 ActivePlayer 透出
//   - 文字、tags、unmute 按鈕照常
// ============================================================

interface Props {
  moment: MomentView
  index: number
  isActive: boolean
  muted: boolean
  onUnmute: () => void
  showBackdrop?: boolean
  canEdit?: boolean
  onEdit?: () => void
  /** 公開分享 — 所有訪客可見；觸發系統分享面板或退回複製連結 modal */
  onShare?: () => void
}

const TEXT_SHADOW = { textShadow: '0 2px 16px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.8)' }

export const MomentCard = forwardRef<HTMLElement, Props>(function MomentCard(
  { moment, index, isActive, muted, onUnmute, showBackdrop = true, canEdit, onEdit, onShare },
  ref
) {
  return (
    <article
      ref={ref}
      data-moment-id={moment.id}
      // h 改用「main 實際可用高度」= 100dvh - header(~3.25rem)，避免每張卡比 main 還高
      // 造成 snap 滾完之後 bottom 還有 ~50px 沒進可視範圍 → 標題被切。
      className="relative w-full overflow-hidden text-paper"
      style={{ height: 'calc(100dvh - 3.25rem)' }}
    >
      {/* 縮圖 backdrop — active 時淡出讓 ActivePlayer 透出 */}
      {showBackdrop && (
        <div
          aria-hidden
          className="absolute inset-0 overflow-hidden bg-ink transition-opacity duration-300"
          style={{ opacity: isActive ? 0 : 1 }}
        >
          {/* Ambient blur 層：縮圖高斯模糊填滿 letterbox */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://i.ytimg.com/vi/${moment.youtubeId}/maxresdefault.jpg)`,
              filter: 'blur(40px) brightness(0.55) saturate(110%)',
              transform: 'scale(1.15)'
            }}
          />
          {/* Contain 縮圖：16:9 居中，永不裁切 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="relative"
              style={{
                width: 'min(100vw, calc(100dvh * 16 / 9))',
                aspectRatio: '16 / 9'
              }}
            >
              <img
                src={`https://i.ytimg.com/vi/${moment.youtubeId}/maxresdefault.jpg`}
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).src = `https://i.ytimg.com/vi/${moment.youtubeId}/hqdefault.jpg`
                }}
                alt=""
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {/* 底部漸層：只在下半部加深，留給標題閱讀。上半部保持清透讓影片不被罩。 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-1/2 bg-gradient-to-t from-ink/95 via-ink/55 to-transparent"
      />

      {/* 頂部左：分類 chip + 序號 — z-20 確保在 title overlay 之上 */}
      <div className="absolute left-6 top-6 z-20 flex items-center gap-3 md:left-16 md:top-10">
        {moment.category && (
          <span
            className="inline-flex items-center rounded-full border bg-ink/30 px-3 py-1 text-xs font-medium backdrop-blur"
            style={{
              color: moment.category.color,
              borderColor: `${moment.category.color}80`
            }}
          >
            {moment.category.name}
          </span>
        )}
        <span className="text-xs tracking-[0.2em] text-paper/75" style={TEXT_SHADOW}>
          #{String(index + 1).padStart(2, '0')}
        </span>
      </div>

      {/* 頂部右：分享（公開）+ 編輯（admin）— z-20 必要！title block 的 z-10 padding 區會吃 click，提到 z-20 才點得到 */}
      <div className="absolute right-4 top-6 z-20 flex items-center gap-2 md:right-8 md:top-10">
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="flex h-11 w-11 items-center justify-center rounded-full
                       border border-paper/20 bg-ink/40 text-paper backdrop-blur transition hover:bg-ink/60"
            aria-label="分享這個片刻"
            title="分享這個片刻"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="M8.6 13.5 L15.4 17.5 M15.4 6.5 L8.6 10.5" />
            </svg>
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="flex h-11 w-11 items-center justify-center rounded-full
                       border border-paper/20 bg-ink/40 text-paper backdrop-blur transition hover:bg-ink/60"
            aria-label="編輯此感動點"
          >
            ✏️
          </button>
        )}
      </div>

      {/* 底部：策展文字 overlay
          - 改用 absolute bottom-0：標題永遠貼底，不再用 flex items-end + 巨大 pt 把自己撐高
          - pointer-events-none 在外層：padding 不再吃 click（之前 z-10 padding 蓋住 pencil）
          - pointer-events-auto 在內層：文字選取/連結仍可用 */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-6 pb-10 md:px-16 md:pb-14">
        <div className="pointer-events-auto max-w-reading">
          <h2
            className="font-serif text-3xl font-bold leading-tight tracking-tightest text-paper md:text-5xl"
            style={{ ...TEXT_SHADOW, opacity: isActive ? 1 : 0.75 }}
          >
            {moment.title}
          </h2>

          {moment.note && (
            <p className="mt-5 text-base leading-relaxed text-paper/90 md:text-lg" style={TEXT_SHADOW}>
              {moment.note}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-paper/70">
            {moment.video?.originalTitle && (
              <span style={TEXT_SHADOW}>
                來源：{moment.video.originalTitle}
                {moment.video.year && ` · ${moment.video.year}`}
              </span>
            )}
          </div>

          {(moment.tags?.length ?? 0) > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {moment.tags.map((tag) => (
                <span key={tag} className="text-xs text-paper/60" style={TEXT_SHADOW}>
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 焦點 + muted → 浮動 unmute 提示 */}
      {isActive && muted && (
        <button
          onClick={onUnmute}
          className="absolute inset-x-0 bottom-6 z-20 mx-auto flex w-fit items-center gap-2 rounded-full
                     bg-paper/95 px-4 py-2 text-sm font-medium text-ink shadow-xl hover:bg-paper"
        >
          <span aria-hidden>♪</span> 點此解除靜音
        </button>
      )}
    </article>
  )
})
